import React from "react";

import { EventGate } from "~/components/event-gate";
import { useFormContext } from "~/components/form/use-form-context";
import type { Slottable } from "~/components/slot";
import { type VariantProp, type VariantProps, cv } from "~/tools/class-variance";

/**
 * Check out Figma for the design of the button:
 *
 * @link https://www.figma.com/design/YIkZms7ygvgqXYnbzDFr8Y/%23-StackState-DS?node-id=4373-1487&t=NuoRgRBOmZMDr71v-4
 */
const variants = cv("button [&_svg]:pointer-events-none [&_svg]:shrink-0", {
	variants: {
		variant: {
			neutral: "",
			primary: "button-primary",
			secondary: "button-secondary",
			tertiary: "button-tertiary",
			link: "underline hover:no-underline",
		},

		size: {
			"3xs": "button-3xs",
			"2xs": "button-2xs",
			xs: "button-xs",
			sm: "button-sm",
			md: "",
			lg: "button-lg",
		},

		icon: {
			false: "[&_svg]:text-10",
			true: "button-square",
		},

		layout: {
			inline: "",
			block: "button-block",
		},
	},
});

export type ButtonVariant = VariantProp<typeof variants, "variant">;

export type ButtonSize = VariantProp<typeof variants, "size">;

export interface ButtonProps extends Slottable, VariantProps<typeof variants>, React.ButtonHTMLAttributes<HTMLElement> {
	loading?: boolean;
	disabled?: boolean;
	active?: boolean;
}

export const Button = React.forwardRef<HTMLElement, ButtonProps>(
	({ asChild = false, className, variant, size, layout, icon, active, disabled, loading, children, ...props }, ref) => {
		const form = useFormContext();
		const listening = !(disabled || loading);

		return (
			<EventGate
				aria-busy={loading || undefined}
				aria-disabled={disabled || undefined}
				className={variants({ variant, size, icon, layout, className })}
				data-state={active ? "active" : undefined}
				listening={listening}
				ref={ref}
				{...props}
			>
				{asChild ? (
					children
				) : (
					<button
						form={props.form ?? form?.id}
						// if the button is not listening, the type is set to "button" to prevent form submission
						type={listening ? (props.type ?? "button") : "button"}
					>
						{children}
					</button>
				)}
			</EventGate>
		);
	},
);

if (import.meta.env.DEV) {
	Button.displayName = "Button";
}
